import { defineStore } from "pinia";
import type { Account, OperationStateChange } from "@/types";
import { getAccountsSum } from '@/auth/getAccountsSum';
import { getAllAccounts } from '@/auth/getAllAccounts';
import { getTotalDocuments } from "@/auth/getTotalDocuments";
import { setNewTotal } from "@/setNewTotal";

type StateShape = {
	accountsList: Array<Account>,
	accountsListDemo: Array<Account>,
	total: number | string | null,
	totalDemo: number | string | null,
	incomesTotalDemo: number | string | null,
	incomesTotal: number | string | null,
	spendingTotalDemo: number | string | null,
	spendingTotal: number | string | null,
	meta: {
		isDemoMode: boolean,
		isLoading: boolean,
		hasError: boolean,
		totalFetchedDocuments: null | number,
		isBankAccountTotalLoading: boolean,
		hasSomeAccounts: null | boolean,
	}
};

export const useNewAccountStore = defineStore({
	id: "account",
	state: (): StateShape => ({
		accountsList: [],
		accountsListDemo: [],
		total: null,
		totalDemo: null,
		incomesTotal: null,
		incomesTotalDemo: null,
		spendingTotal: null,
		spendingTotalDemo: null,
		meta: {
			isDemoMode: false,
			isLoading: true,
			hasError: false,
			totalFetchedDocuments: null,
			isBankAccountTotalLoading: true,
			hasSomeAccounts: null,
		},
	}),

	getters: {
		getIsLoading(): boolean {
			return this.meta.isLoading;
		},

		getIsBankAccountTotalLoading(): boolean {
			return this.meta.isBankAccountTotalLoading;
		},

		getHasError(): boolean {
			return this.meta.hasError;
		},

		getHasMoreDocuments(): boolean {
			if (this.meta.isDemoMode) {
				return this.accountsListDemo.length === 3 ? false : true
			}

			return (this.accountsList.length !== this.meta.totalFetchedDocuments || this.accountsList.length === 1);
		},

		getAccounts(): Array<Account> {
			return this.meta.isDemoMode ? this.accountsListDemo : this.accountsList;
		},

		getHasSomeAccounts(): null | boolean {
			return this.meta.isDemoMode ? false : this.meta.hasSomeAccounts;
		},

		getAccountById: (state) => {
			return (bankAccountId: string | undefined) => {
				return state[state.meta.isDemoMode ? 'accountsListDemo' : 'accountsList'].find((account) => account.$id.toString() === bankAccountId);
			};
		},

		getAccountsListSorted: (state) => {
			return (key: 'total' | 'incomesTotal' | 'spendingTotal') => {
				const arrayToSort = state.meta.isDemoMode ? [...state.accountsListDemo] : [...state.accountsList]
				return arrayToSort.sort((a, b) => {
					const aValue = a[key]
					const bValue = b[key]

					if (typeof aValue !== 'number' || typeof bValue !== 'number') return 0
					if (key === 'total' || key === 'incomesTotal') return (bValue as number) - (aValue as number)
					return (aValue as number) - (bValue as number)
				})
			}
		},

		getTotal(): number | string | null {
			return this.meta.isDemoMode ? this.totalDemo : this.total;
		},

		getIncomesTotal(): number | string | null {
			return this.meta.isDemoMode ? this.incomesTotalDemo : this.incomesTotal;
		},

		getSpendingTotal(): number | string | null {
			return this.meta.isDemoMode ? this.spendingTotalDemo : this.spendingTotal;
		},
		getIsDemoMode(): boolean {
			return this.meta.isDemoMode
		}
	},

	actions: {
		async fetchAllAccounts(ids?: string[], mode: 'include' | 'exclude' = 'include') {
			if (!this.getHasMoreDocuments) return

			const result = await getAllAccounts(mode, { is_demo: this.meta.isDemoMode, ...(ids && { accountsId: ids }) });

			this.meta.isLoading = false;
			this.meta.hasError = result ? false : true;

			if (!result) return

			const targetList = this.meta.isDemoMode ? this.accountsListDemo : this.accountsList;

			if (mode === "exclude") {
				targetList.push(...result.documents);
				this.meta.totalFetchedDocuments = targetList.length;
			} else {
				if (this.meta.isDemoMode) {
					this.accountsListDemo = result.documents;
				} else {
					this.accountsList = result.documents;
				}

				this.meta.totalFetchedDocuments = result.total ? result.total : null;
			}
		},

		async setHasSomeAccount() {
			if (this.getIsDemoMode) {
				this.meta.hasSomeAccounts = null
				return
			}

			if (this.getAccounts.length) {
				if (this.meta.hasSomeAccounts) return;
				this.meta.hasSomeAccounts = true;
			}

			if (this.getHasSomeAccounts === null) {
				const result = await getTotalDocuments('bankAccount', {is_demo: false});
				this.meta.hasSomeAccounts = Boolean(result.total);
			}
		},

		setDemoMode(value: boolean) {
			this.meta.isDemoMode = value
		},

		async setAccountsSum() {
			const batchList = this.meta.isDemoMode ? 'accountsListDemo' : 'accountsList'

			await Promise.all(this[batchList].map(async (account, index) => {
				const params = {
					id: account.$id.toString(),
					is_demo: this.meta.isDemoMode
				}

				const result = await getAccountsSum(params);
				const incomesResult = await getAccountsSum({...params, type: "credit"});
				const spendingResult = await getAccountsSum({...params, type: "debit"});

        if (this[batchList][index]) {
  				this[batchList][index].total = result === undefined || result.accounts[0] === undefined ? '-' : result.accounts[0].total;
  				this[batchList][index].incomesTotal = result === undefined || incomesResult.accounts[0] === undefined ? '-' : incomesResult.accounts[0].total;
  				this[batchList][index].spendingTotal = result === undefined || spendingResult.accounts[0] === undefined ? '-' : Math.abs(spendingResult.accounts[0].total);
        }
			}))

			this.meta.isBankAccountTotalLoading = false;
		},

		async setAllAccountsSum() {
			if (this.getTotal !== null) return;
			if (this.getIncomesTotal !== null) return;
			if (this.getSpendingTotal !== null) return;

			const result = await getAccountsSum({is_demo: this.meta.isDemoMode});
			const incomesResult = await getAccountsSum({ type: 'credit', is_demo: this.meta.isDemoMode })
			const spendingResult = await getAccountsSum({ type: 'debit', is_demo: this.meta.isDemoMode })

			this[this.meta.isDemoMode ? 'totalDemo' : 'total'] = result === undefined ? '-' : result.total;
			this[this.meta.isDemoMode ? 'incomesTotalDemo' : 'incomesTotal'] = incomesResult === undefined ? '-' : incomesResult.total;
			this[this.meta.isDemoMode ? 'spendingTotalDemo' : 'spendingTotal'] = spendingResult === undefined ? '-' : spendingResult.total;
		},

		setAccountTotals(operationState: OperationStateChange) {
			const accountIndex = this.accountsList.findIndex(item => item.$id === operationState.newOperation.bank_account.$id);
			if (accountIndex === -1) return;

			const key = operationState.newOperation.type === 'debit' ? "spendingTotal" : "incomesTotal";
			const total = this.getAccounts[accountIndex].total
			const totalType = this.accountsList[accountIndex][key] === '-' || this.accountsList[accountIndex][key] === undefined ? 0 : this.accountsList[accountIndex][key];
			const newTotal = setNewTotal(total === undefined ? '-' : total, operationState);

			if (operationState.type === "added") {
				this.accountsList[accountIndex][key] = totalType + operationState.newOperation.amount;
			}

			if (operationState.type === 'updated') {
				if (operationState.fieldsTouched) {
					if (operationState.fieldsTouched.hasOwnProperty('amount')) {
						if (!operationState.fieldsTouched.type) {
							const value = this.accountsList[accountIndex][key]

							if (typeof value === 'number') {
								this.accountsList[accountIndex][key] = value + (operationState.newOperation.amount - (operationState.oldOperation?.amount ?? 0));
							}
						}
					}

					if (operationState.fieldsTouched.type) {
						const incomesTotal = this.accountsList[accountIndex].incomesTotal === '-' || this.accountsList[accountIndex].incomesTotal === undefined ? 0 : this.accountsList[accountIndex].incomesTotal
						const spendingTotal = this.accountsList[accountIndex].spendingTotal === '-' || this.accountsList[accountIndex].spendingTotal === undefined ? 0 : this.accountsList[accountIndex].spendingTotal

						if (typeof incomesTotal === 'number') {
							this.accountsList[accountIndex].incomesTotal = operationState.fieldsTouched.type === 'debit' ?
								incomesTotal - (operationState.oldOperation?.amount ?? 0) :
								incomesTotal + operationState.newOperation.amount
						}

						if (typeof spendingTotal === 'number') {
							this.accountsList[accountIndex].spendingTotal = operationState.fieldsTouched.type === 'debit' ?
								spendingTotal + operationState.newOperation.amount :
								spendingTotal - (operationState.oldOperation?.amount ?? 0)
						}
					}
				}
			}

			if (operationState.type === "deleted") {
				if (typeof totalType !== 'number') return

				this.accountsList[accountIndex][key] = totalType - operationState.newOperation.amount;
			}

			if (typeof newTotal === 'number') {
				this.updateBankAccountTotal(accountIndex, newTotal);
			}
		},

		async enrichAccountsList(accountId?: string[], mode: "include" | "exclude" = 'include') {
			if (this.getHasMoreDocuments) {
				if (mode === "exclude") {
					this.meta.isLoading = true;
					this.meta.isBankAccountTotalLoading = true;
				}

				await this.fetchAllAccounts(accountId, mode);
				await this.setAccountsSum();
			}
		},

		setNewBankAccountName(bankAccountId: string, name: string) {
		  if (bankAccountId !== undefined) {
        const account = this.getAccountById(bankAccountId);
        if (account) account.name = name;
			}
		},

		updateBankAccountTotal(index: number, newTotal: number) {
			this.accountsList[index].total = newTotal;

			if (this.getTotal !== null) {
				const totalsValues = this.accountsList.reduce((prev, next) => {
					prev.total = typeof next.total === 'number' ? prev.total + next.total : prev.total
					prev.incomesTotal = typeof next.incomesTotal === 'number' ? prev.incomesTotal + next.incomesTotal : prev.incomesTotal
					prev.spendingTotal = typeof next.spendingTotal === 'number' ? prev.spendingTotal + next.spendingTotal : prev.spendingTotal

					return prev;
				},
					{ total: 0, incomesTotal: 0, spendingTotal: 0 });

				this.total = totalsValues.total;
				this.incomesTotal = totalsValues.incomesTotal;
				this.spendingTotal = totalsValues.spendingTotal;
			}
		},

		pushNewAccount(account: Account) {
			account.total = 0;
			account.spendingTotal = 0;
			account.incomesTotal = 0;
			this.meta.totalFetchedDocuments = this.meta.totalFetchedDocuments && this.meta.totalFetchedDocuments + 1;

			this.accountsList.unshift(account);
		},
	},
});
